import axios from 'axios'
import qs from 'qs'
import { toast } from 'sonner'
import { AuthService } from '../auth'
import type { AxiosError, InternalAxiosRequestConfig } from 'axios'
import type { Session } from '@/types/auth'
import { getJsonCookie, setJsonCookie } from '@/lib/cookies'
import { clearSession, createSession } from '@/lib/session'

const APIService = axios.create({
  baseURL: import.meta.env.VITE_API_ENDPOINT,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
})

APIService.interceptors.request.use(
  (request) => {
    if (!request.params) {
      request.params = {}
    }

    const authData = getJsonCookie('authentication')
    const token = authData?.accessToken

    request.baseURL = import.meta.env.VITE_API_ENDPOINT
    request.paramsSerializer = (params: any) =>
      qs.stringify(
        Object.fromEntries(
          Object.entries(params).map(([k, v]) => [k, v === null ? 'null' : v]),
        ),
        { skipNulls: false, strictNullHandling: true },
      )

    if (token) {
      request.headers['Authorization'] = `Bearer ${token}`
    }

    // Always set x-currency to "vi"
    request.headers['x-currency'] = 'vi'

    return request
  },
  (error) => {
    return Promise.reject(error)
  },
)

let isRefreshing = false
let failedQueue: {
  resolve: (value: unknown) => void
  reject: (reason?: any) => void
}[] = []

const processQueue = (
  error: AxiosError | null,
  token: string | null = null,
) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })

  failedQueue = []
}

APIService.interceptors.response.use(
  (response) => {
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (originalRequest.url?.includes('/auth/admin-login')) {
        return Promise.reject(error)
      }

      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject })
        })
          .then((token) => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token
            return APIService(originalRequest)
          })
          .catch((err) => {
            return Promise.reject(err)
          })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const { data: refreshedData } = await AuthService.refreshToken()
        const newPayload = {
          user: refreshedData.user,
          accessToken: refreshedData.token.accessToken,
          refreshToken: refreshedData.token.refreshToken,
          accessExpiresIn: refreshedData.token.accessExpiresIn,
          accessExpiresAt: refreshedData.token.accessExpiresAt,
        }

        await createSession(newPayload as Session)
        setJsonCookie('authentication', newPayload, { expires: 30 })

        originalRequest.headers['Authorization'] =
          `Bearer ${newPayload.accessToken}`
        processQueue(null, newPayload.accessToken)
        return APIService(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError as AxiosError, null)
        setJsonCookie('authentication', null, { expires: -1 }) // Clear the cookie
        clearSession()
        window.location.href = '/login'
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    // Handle 403 Forbidden — show toast and redirect to login
    if (error.response?.status === 403) {
      const message =
        (error.response.data as any)?.message || 'Bạn không có quyền truy cập'
      toast.error(message)
      clearSession()
      setJsonCookie('authentication', null, { expires: -1 })
      window.location.href = '/login'
      return Promise.reject(error)
    }

    return Promise.reject(error)
  },
)

export default APIService
